/**
 * LegalShell — minimal chrome for static legal documents
 * (Privacy Policy, Terms of Service).
 *
 * Deliberately decoupled from AppShell:
 *  - These pages must be reachable WITHOUT authentication (compliance,
 *    deep-linking from external emails, GDPR access requests).
 *  - The full app chrome (sidebar with workspace nav, topbar with
 *    user menu) would be confusing on a legal page and would fail to
 *    render for logged-out visitors anyway.
 *
 * The shell provides: a top bar with the FrameFlow wordmark + a
 * "Back to app" / "Sign in" link, a centered narrow-column reading
 * surface, and a slim footer with the contact email and a "Last
 * updated" stamp.
 *
 * Children are rendered as the document body inside an
 * `<article className="prose">`-style container — h1/h2/h3/p styling
 * is provided here so individual pages can focus purely on content.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LegalShell = ({
  eyebrow,
  title,
  effectiveDate,
  lastUpdated,
  children,
}) => {
  const { user } = useAuth() || {};

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="legal-shell">
      {/* Top bar */}
      <header
        className="border-b border-border bg-background/60 backdrop-blur-sm sticky top-0 z-20"
        data-testid="legal-top"
      >
        <div className="container mx-auto px-4 md:px-6 h-16 flex items-center justify-between max-w-5xl">
          <Link
            to="/"
            className="flex items-center gap-2 font-display font-bold text-lg tracking-tight hover:text-primary transition-colors"
            data-testid="legal-wordmark"
          >
            <FileText className="size-5 text-primary" />
            FrameFlow
          </Link>
          <Link
            to={user ? '/' : '/auth'}
            className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground hover:text-foreground transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="size-3.5" />
            {user ? 'Back to app' : 'Sign in'}
          </Link>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 container mx-auto px-4 md:px-6 py-10 md:py-14 max-w-3xl">
        {/* Hero */}
        <div className="mb-10 md:mb-12">
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-primary mb-3">
            {eyebrow}
          </p>
          <h1
            className="font-display font-bold tracking-tight text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-4"
            data-testid="legal-hero-title"
          >
            {title}
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            {effectiveDate && (
              <span>
                <span className="font-semibold text-foreground/70">Effective:</span>{' '}
                {effectiveDate}
              </span>
            )}
            {lastUpdated && (
              <span>
                <span className="font-semibold text-foreground/70">Last updated:</span>{' '}
                {lastUpdated}
              </span>
            )}
          </div>
        </div>

        {/* Document content — passes through styling utilities so
            individual pages stay markup-focused. */}
        <article
          className="
            text-foreground/85 leading-relaxed
            [&_h2]:font-display [&_h2]:font-bold [&_h2]:tracking-tight
            [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:text-foreground
            [&_h2]:mt-10 [&_h2]:mb-3
            [&_h3]:font-display [&_h3]:font-semibold [&_h3]:tracking-tight
            [&_h3]:text-base [&_h3]:text-foreground
            [&_h3]:mt-6 [&_h3]:mb-2
            [&_p]:mb-4 [&_p]:text-[15px]
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1.5
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1.5
            [&_li]:text-[15px]
            [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2
            [&_a:hover]:text-primary/80
            [&_strong]:font-semibold [&_strong]:text-foreground
          "
          data-testid="legal-body"
        >
          {children}
        </article>
      </main>

      {/* Slim footer */}
      <footer
        className="border-t border-border bg-background/60 py-5 px-4 md:px-6"
        data-testid="legal-footer"
      >
        <div className="container mx-auto max-w-5xl flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4 text-[11px] tracking-[0.16em] uppercase text-muted-foreground">
          <span>© {new Date().getFullYear()} FrameFlow · Operated by Cinerian</span>
          <div className="flex items-center gap-4">
            <Link
              to="/privacy"
              className="hover:text-primary transition-colors"
              data-testid="legal-footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-primary transition-colors"
              data-testid="legal-footer-terms-link"
            >
              Terms of Service
            </Link>
            <Link
              to="/help"
              className="hover:text-primary transition-colors"
              data-testid="legal-footer-help-link"
            >
              Help Center
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalShell;
